/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from "react";
import { Link as RouterLink } from "react-router-dom";

export interface PromoBannerProps {
  sources: Array<{
    /** Imported SVG asset (text baked in). */
    src: string;
    /** Min viewport width (px) this artwork is served at. */
    minWidth?: number;
  }>;
  /** Accessible description — carries the baked-in headline for screen readers. */
  alt: string;
  testId: string;
  /** When set, the whole banner becomes a clickable in-app link (the baked CTA is just artwork). */
  linkTo?: string;
  linkTestId?: string;
}

const PromoBanner: React.FC<PromoBannerProps> = ({ sources, alt, testId, linkTo, linkTestId }) => {
  if (!sources.length) {
    return null;
  }

  const ordered = [...sources].sort((a, b) => (b.minWidth ?? 0) - (a.minWidth ?? 0));
  const fallback = ordered[ordered.length - 1];

  const banner = (
    <picture>
      {ordered
        .filter((s) => s.minWidth)
        .map((s) => (
          <source key={s.minWidth} media={`(min-width: ${s.minWidth}px)`} srcSet={s.src} />
        ))}
      <img src={fallback.src} alt={alt} className="block w-full rounded-md" data-testid={testId} />
    </picture>
  );

  if (!linkTo) {
    return banner;
  }

  return (
    <RouterLink
      to={linkTo}
      data-testid={linkTestId}
      className={`
        block cursor-pointer rounded-lg transition-shadow duration-200
        hover:shadow-[0_5px_25px_-5px_rgba(0,0,0,0.25)]
        focus-visible:ring-2 focus-visible:ring-forest-green-950 focus-visible:ring-offset-2
        focus-visible:outline-hidden
        motion-reduce:transition-none
      `}
    >
      {banner}
    </RouterLink>
  );
};

export default PromoBanner;
